import { ReflectUtils } from "@dooboostore/core";
import { ensureInit } from "./elementDefine.js";
import { SwcUtils } from "../utils/Utils.js";
import { buildSwcParameterArgs, buildCommonKindValues } from "./parameter.js";
const SET_INTERVAL_METADATA_KEY = Symbol.for("simple-web-component:set-interval");
const getActiveEntries = (inst) => inst.__swc_intervalIds ?? (inst.__swc_intervalIds = []);
function setInterval(interval, options = {}) {
  return (target, propertyKey, descriptor) => {
    const constructor = target.constructor;
    const normalizedOptions = { ...options, type: options.type ?? "onConnected" };
    let metaList = ReflectUtils.getOwnMetadata(SET_INTERVAL_METADATA_KEY, constructor);
    if (!metaList) {
      metaList = [];
      ReflectUtils.defineMetadata(SET_INTERVAL_METADATA_KEY, metaList, constructor);
    }
    metaList.push({ propertyKey, interval, options: normalizedOptions });
    if (normalizedOptions.type === "onConnected" || !descriptor) {
      return;
    }
    const original = descriptor.value;
    descriptor.value = function(...args) {
      const res = original.apply(this, args);
      const vk = normalizedOptions.valueKey ?? SET_INTERVAL_METADATA_KEY;
      const tickFn = typeof res === "function" ? res : res && typeof res === "object" && typeof res[vk] === "function" ? res[vk] : void 0;
      if (tickFn) {
        ensureInit(this);
        const win = SwcUtils.resolveWindow(this);
        const id = win.setInterval(() => {
          try {
            tickFn(id);
          } catch (e) {
            console.error("[SWC] setInterval tick error:", e);
          }
        }, interval);
        normalizedOptions.created?.(SwcUtils.getHelperAndHostSet(this), id);
        getActiveEntries(this).push(id);
      }
      return res;
    };
    return descriptor;
  };
}
const findAllSetIntervalMetadata = (target) => {
  const constructor = typeof target === "function" ? target : target.constructor;
  return (ReflectUtils.findAllMetadata(SET_INTERVAL_METADATA_KEY, constructor) || []).flat();
};
class SetIntervalLifeCycler {
  onConnected(helperHostSet) {
    const inst = helperHostSet.$this;
    inst.__swc_intervalIds = [];
    for (const meta of findAllSetIntervalMetadata(inst)) {
      if (meta.options.type !== "onConnected") continue;
      const id = helperHostSet.$w.setInterval(() => {
        void (async () => {
          const helper = SwcUtils.getHelperAndHostSet(inst);
          if (meta.options.filter && !await meta.options.filter(helper)) return;
          const paramArgs = meta.options.parameter?.(helperHostSet) ?? [];
          const common = buildCommonKindValues(inst, helperHostSet.$w);
          const buildArgs = (beforeReturn) => buildSwcParameterArgs(inst, meta.propertyKey, {
            ...common,
            setIntervalBeforeReturn: beforeReturn
          }, [...paramArgs, beforeReturn]);
          let args = buildArgs(void 0);
          if (meta.options.before) {
            const br = await meta.options.before(helper);
            args = buildArgs(br);
          }
          let result, error;
          try {
            result = await inst[meta.propertyKey](...args);
            const vk = meta.options.valueKey ?? SET_INTERVAL_METADATA_KEY;
            if (result && typeof result === "object" && typeof result[vk] === "function") result[vk](id);
          } catch (e) {
            error = e;
          } finally {
            if (meta.options.finally) await meta.options.finally(helper, { args, result, error });
          }
          if (error) console.error("[SWC] setInterval tick error:", error);
        })().catch((e) => console.error("[SWC] setInterval tick error:", e));
      }, meta.interval);
      meta.options.created?.(helperHostSet, id);
      getActiveEntries(inst).push(id);
    }
  }
  onDisconnected(helperHostSet) {
    const inst = helperHostSet.$this;
    for (const id of inst.__swc_intervalIds ?? []) {
      try {
        helperHostSet.$w.clearInterval(id);
      } catch (e) {
        console.error("[SWC] clearInterval error:", e);
      }
    }
    inst.__swc_intervalIds = [];
  }
}
const setIntervalReturnValue = (interval, options = {}) => setInterval(interval, { ...options, type: "returnValue" });
export {
  SET_INTERVAL_METADATA_KEY,
  SetIntervalLifeCycler,
  findAllSetIntervalMetadata,
  setInterval,
  setIntervalReturnValue
};
//# sourceMappingURL=setInterval.js.map
